import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Crosshair } from "lucide-react";
import { useGame } from "../game/em2c-store";
import { EM2C_BUILDINGS } from "../game/data/em2c-buildings";
import { getBuildingRecipesForType } from "../game/systems/UnifiedSimulation";
import { formatResourceAmount, formatResourceRate } from "../game/resourceUnits";

// One row per building type, with individual instances revealed on demand.
// Rates are nominal simulation rates; blocked/idle buildings contribute zero.
export default function ResourceFlowDetails({ resourceKey, label }) {
  const buildings = useGame(s => s.buildings);
  const colonists = useGame(s => s.colonists);
  const selectedRecipes = useGame(s => s.selectedRecipes || {});
  const idleIds = useGame(s => s._autoIdleIds || []);
  const resources = useGame(s => s.resources);
  const netRates = useGame(s => s.netRates || {});
  const selectBuilding = useGame(s => s.selectBuilding);
  const [expanded, setExpanded] = useState({});
  const data = useMemo(() => {
    const groups = new Map();
    buildings.forEach((b, index) => {
      const def = EM2C_BUILDINGS[b.type];
      if (!def) return;
      const recipes = getBuildingRecipesForType(b.type);
      const recipe = recipes.find(r => r.id === selectedRecipes[b.id]);
      const inputs = recipe?.inputs || def.productionInputs || {};
      const outputs = recipe?.outputs || def.productionOutputs || {};
      const factor = ["oxygen", "water", "food", "raw_water", "hydrogen"].includes(resourceKey) ? 0.01 : 1;
      const producing = Number(outputs[resourceKey] || 0) * factor;
      const consuming = Number(inputs[resourceKey] || 0) * factor
        + (resourceKey === "power" ? Number(def.powerConsumption || 0) : 0)
        + (resourceKey === "water" ? Number(def.waterConsumption || 0) : 0)
        + (resourceKey === "oxygen" ? Number(def.oxygenConsumption || 0) : 0);
      if (!producing && !consuming) return;
      const isRunning = b.progress >= 1 && b.active !== false && !idleIds.includes(b.id)
        && !["output_full", "input_shortage", "no_power", "no_water", "no_oxygen", "power_shed", "no_workers", "auto_idle"].includes(b.state);
      if (!groups.has(b.type)) groups.set(b.type, { type: b.type, name: def.name, entries: [], producing: 0, consuming: 0 });
      const group = groups.get(b.type);
      group.entries.push({ b, num: b.buildNumber || index + 1, producing, consuming, running: isRunning });
      if (isRunning) {
        group.producing += producing;
        group.consuming += consuming;
      }
    });
    const rows = [...groups.values()];
    return {
      producers: rows.filter(g => g.entries.some(x => x.producing > 0)),
      consumers: rows.filter(g => g.entries.some(x => x.consuming > 0)),
    };
  }, [buildings, selectedRecipes, resourceKey, idleIds]);

  const locate = b => {
    selectBuilding(b.id);
    window.dispatchEvent(new CustomEvent("mars:focus-building", { detail: { x: b.x, z: b.z, q: b.q, r: b.r } }));
  };

  const section = (groups, heading, direction) => (
    <section className="min-w-0">
      <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300 pb-1 border-b border-white/10">{heading}</div>
      <div className="space-y-1 mt-1">
        {groups.length === 0 && <div className="text-[10px] text-slate-500">No building currently configured</div>}
        {groups.map(g => {
          const key = direction + g.type;
          const isOpen = expanded[key];
          const activeCount = g.entries.filter(e => e.running).length;
          const sum = direction === "produce" ? g.producing : g.consuming;
          return <div key={key} className="border border-white/10 bg-white/[0.03]">
            <button onClick={() => setExpanded(s => ({ ...s, [key]: !s[key] }))} aria-expanded={Boolean(isOpen)}
              className="w-full flex items-center gap-2 text-left p-2 text-xs">
              {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              <span className="flex-1 text-slate-200 truncate">{g.name} ×{g.entries.length}</span>
              <span className="text-[10px] text-slate-500">{activeCount} running</span>
              <span className={direction === "produce" ? "text-green-400" : "text-orange-400"}>{direction === "produce" ? "+" : "-"}{formatResourceRate(resourceKey, sum)}</span>
            </button>
            {isOpen && <div className="border-t border-white/10 p-1 space-y-1">
              {g.entries.sort((a,b) => a.num-b.num).map(e => <button key={e.b.id} onClick={() => locate(e.b)} className="w-full flex items-center gap-2 text-left px-2 py-1 hover:bg-cyan-400/10 text-[10px]">
                <Crosshair size={12} className="text-cyan-400" />
                <span className="flex-1 text-slate-300">#{e.num} {e.running ? "Running" : (e.b.state || "Idle")}</span>
                <span className="text-slate-500">{formatResourceRate(resourceKey, direction === "produce" ? e.producing : e.consuming)} nominal</span>
              </button>)}
            </div>}
          </div>;
        })}
      </div>
    </section>
  );

  const res = resources[resourceKey];
  return <div className="p-3 space-y-3" data-testid="resource-flow-details">
    <div className="flex items-center justify-between">
      <div>
        <div className="text-xs uppercase font-bold text-white">{label}</div>
        <div className="text-[10px] font-mono text-slate-400">
          {formatResourceAmount(resourceKey, res?.stored || 0)} / {formatResourceAmount(resourceKey, res?.max || 0)} capacity · Net {formatResourceRate(resourceKey, Number(netRates[resourceKey] || 0))}
        </div>
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {section(data.producers, "Producing", "produce")}
      <div>
        {section(data.consumers, "Consuming", "consume")}
        {["oxygen", "water", "food"].includes(resourceKey) && <div className="mt-2 text-[10px] border border-white/10 p-2 text-slate-300">
          Colonists: {colonists.length} · life-support consumption is included in the total net rate above.
        </div>}
      </div>
    </div>
    <div className="text-[9px] text-slate-500">Rates shown per simulated sol. Current contributions exclude disabled, idle and blocked buildings. Open a group to locate an individual structure.</div>
  </div>;
}
